import { motion } from "framer-motion";
import { Bookmark, BookmarkCheck, Trash2, GripVertical, Clock, Users, Sun, CalendarIcon, Info, Car } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { formatDurationHours } from "@/lib/wizard-types";
import type { ReactNode } from "react";

export interface EventCardData {
  id: string;
  title: string;
  description: string;
  category: string;
  venue_name: string;
  venue_address?: string;
  estimated_duration: string;
  cost_level: string;
  cost_details: string;
  drive_minutes_from_lwr?: number;
  crowd_level?: string;
  weather_sensitivity?: number;
  indoor_outdoor?: string;
  schedule_notes?: string;
  /** Optional override for the title link. When set, the card title links here verbatim
   *  (e.g. a `https://maps.app.goo.gl/...` short link) instead of the constructed
   *  Google Maps search built from venue_name + venue_address. Used to pin the exact
   *  Google listing when multiple businesses share the same address. */
  map_url?: string;
}

interface EventCardProps {
  event: EventCardData;
  index: number;
  /** Border accent color (cycled by caller). */
  accentColor: string;
  /** Optional schedule pill label (e.g. "Sat–Sun", "Daily", "Mar 2"). */
  scheduleLabel?: string;
  /** Optional reciprocal pass benefit message. */
  reciprocalBenefit?: string | null;
  /** Cost label override — Explore uses formatted cost; Plan uses cost_details/cost_level. */
  costLabel: string;
  /** Action button: "save" (Explore) or "remove" (Plan). */
  mode: "save" | "remove";
  /** Save state (Explore only). */
  saved?: boolean;
  onToggleSave?: () => void;
  onRemove?: () => void;
  /** Drag handle (Plan only). */
  showDragHandle?: boolean;
  /** Click handler for the title link click tracking. */
  onTitleClick?: () => void;
  /** Optional drive-time override (minutes) — used when the user supplied a ZIP. */
  driveMinutes?: number;
  /** Optional drive-time tooltip override (e.g. "from 34232"). */
  driveMinutesLabel?: string;
  /** Optional slot rendered below the pills (e.g. signed-in visit/rating controls). */
  userActions?: ReactNode;
}

/**
 * Shared event card used on Explore and Plan.
 * White background with a colored outline. Title at top, pills at bottom.
 * Right column holds either a Save button (Explore) or a Trash button (Plan).
 */
export function EventCard({
  event,
  index,
  accentColor,
  scheduleLabel,
  reciprocalBenefit,
  costLabel,
  mode,
  saved,
  onToggleSave,
  onRemove,
  showDragHandle,
  onTitleClick,
  driveMinutes,
  driveMinutesLabel,
  userActions,
}: EventCardProps) {
  // Prefer an explicit per-event override (e.g. a `maps.app.goo.gl/...` short link)
  // when set — it pins the exact Google listing in cases where a free-text Maps
  // search would resolve to a different business at the same address. Otherwise
  // fall back to the venue_name + venue_address search query.
  const googleMapsUrl = event.map_url && event.map_url.trim().length > 0
    ? event.map_url
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        event.venue_name + " " + (event.venue_address || ""),
      )}`;

  return (
    <TooltipProvider delayDuration={200}>
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      className="group relative rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 p-4 border-2"
      style={{
        borderColor: accentColor,
        backgroundColor: `color-mix(in oklab, ${accentColor} 12%, white)`,
      }}
    >
      {/* Top row: drag handle + title/description + action button */}
      <div className="flex items-start gap-3">
        {showDragHandle && (
          <div className="mt-1 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors print:hidden shrink-0">
            <GripVertical className="h-5 w-5" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onTitleClick}
            className="text-base sm:text-lg font-bold leading-snug text-foreground hover:underline inline-block"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            {event.title}
          </a>

          <p className="text-sm text-foreground/75 mt-1 leading-snug">
            {event.description}
          </p>
        </div>

        {/* Right action column */}
        {mode === "save" ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggleSave}
                className={`shrink-0 w-9 h-9 rounded-lg border-2 transition-all flex items-center justify-center ${
                  saved
                    ? "bg-primary border-primary text-white shadow-sm"
                    : "border-foreground/20 text-foreground hover:border-primary hover:bg-primary/10"
                }`}
                aria-label={saved ? "Remove from plan" : "Save to plan"}
              >
                {saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{saved ? "Remove from your plan" : "Save to your plan"}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            onClick={onRemove}
            className="shrink-0 w-9 h-9 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all print:hidden flex items-center justify-center"
            aria-label="Remove from plan"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Full-width section below for benefit + pills */}
      <div className={showDragHandle ? "pl-8" : ""}>
        {reciprocalBenefit && (
          <div className="mt-2 px-2.5 py-1.5 rounded-lg text-xs font-bold text-foreground flex items-center gap-1.5 bg-foreground/10">
            <Info className="h-3.5 w-3.5 shrink-0" />
            {reciprocalBenefit}
          </div>
        )}

        {/* Pills — row 1: category, crowd, weather */}
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground cursor-help"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {event.category}
                </span>
              </TooltipTrigger>
              <TooltipContent><p>Category</p></TooltipContent>
            </Tooltip>
            {(() => {
              const mins = typeof driveMinutes === "number"
                ? driveMinutes
                : (typeof event.drive_minutes_from_lwr === "number" ? event.drive_minutes_from_lwr : 0);
              if (!mins || mins <= 0) return null;
              const tooltip = driveMinutesLabel
                ?? "Estimated drive time from Lakewood Ranch";
              return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1 cursor-help">
                    <Car className="h-3 w-3" /> {mins} min
                  </span>
                </TooltipTrigger>
                <TooltipContent><p>{tooltip}</p></TooltipContent>
              </Tooltip>
              );
            })()}
            {event.crowd_level && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1 cursor-help">
                    <Users className="h-3 w-3" /> {event.crowd_level}
                  </span>
                </TooltipTrigger>
                <TooltipContent><p>Typical crowd level</p></TooltipContent>
              </Tooltip>
            )}
            {typeof event.weather_sensitivity === "number" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1 cursor-help">
                    <Sun className="h-3 w-3" />{" "}
                    {event.indoor_outdoor === "indoor"
                      ? "Indoor"
                      : `${event.weather_sensitivity}%`}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px]">
                  <p>
                    <strong>Weather Sensitivity:</strong>{" "}
                    {event.indoor_outdoor === "indoor"
                      ? "Fully indoor — works in any weather."
                      : event.weather_sensitivity <= 30
                      ? "Low — works in almost any weather."
                      : event.weather_sensitivity <= 60
                        ? "Moderate — check conditions."
                        : "High — best on a clear day."}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
        </div>

        {/* Pills — row 2: cost, schedule, time */}
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-highlight text-highlight-foreground cursor-help">
                  {costLabel}
                </span>
              </TooltipTrigger>
              <TooltipContent><p>Estimated cost</p></TooltipContent>
            </Tooltip>
            {scheduleLabel && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-foreground/10 text-foreground cursor-help"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    <CalendarIcon className="h-3 w-3" /> {scheduleLabel}
                  </span>
                </TooltipTrigger>
                <TooltipContent><p>When it runs</p></TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground inline-flex items-center gap-1 cursor-help">
                  <Clock className="h-3 w-3" /> {formatDurationHours(event.estimated_duration)}
                </span>
              </TooltipTrigger>
              <TooltipContent><p>Estimated time to spend</p></TooltipContent>
            </Tooltip>
        </div>

        {userActions}
      </div>
    </motion.div>
    </TooltipProvider>
  );
}